import React from "react";

import { Flex, MultiSelect, Token, useI18nContext } from "@procore/core-react";

import { useGroupContext } from "@/react/providers/group-context-provider";
import { useGetJobTitles, useGetTagOptionsQuery } from "../common/queries/queries";

export type BenchFiltersProps = {
   selectedJobTitles: any[];
   selectedTags: any[];
   onJobTitlesChange: (jobTitles: any[]) => void;
   onTagsChange: (tags: any[]) => void;
};

interface SingleTokenRendererFactoryOptions {
   selectedValues: any[];
   onClearAll: () => void;
   I18n: ReturnType<typeof useI18nContext>;
}

export const createSingleTokenRenderer = ({
   selectedValues,
   I18n,
}: SingleTokenRendererFactoryOptions) => {
   const SingleTokenRenderer = (props: {
      disabled?: boolean;
      focused?: boolean;
      option: any;
      removeToken: () => void;
   }): React.JSX.Element | null => {
      // Only render the token for the first selected item
      if (selectedValues.length > 0 && props.option !== selectedValues[0]) {
         return null;
      }

      return (
         <Token disabled={props.disabled} focused={props.focused}>
            <Token.Label>
               {I18n.t("views.company.workforce_planning.bench.n_selected", {
                  count: selectedValues.length,
               })}
            </Token.Label>
         </Token>
      );
   };

   SingleTokenRenderer.displayName = "SingleTokenRenderer";
   return SingleTokenRenderer;
};

export const BenchFilters: React.FC<BenchFiltersProps> = ({
   selectedJobTitles,
   selectedTags,
   onJobTitlesChange,
   onTagsChange,
}) => {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const {
      data: jobTitles,
      isLoading: isJobTitlesLoading,
      error: isJobTitlesError,
   } = useGetJobTitles(groupId);
   const { data: tags, isLoading: isTagsLoading, error: isTagsError } = useGetTagOptionsQuery();

   return (
      <Flex gap="4px" style={{ marginTop: "4px", marginRight: "6px" }}>
         <MultiSelect
            options={jobTitles ?? []}
            getLabel={(jobTitle) => jobTitle.label}
            value={selectedJobTitles}
            onChange={onJobTitlesChange}
            error={isJobTitlesError ? true : false}
            loading={isJobTitlesLoading}
            placeholder={I18n.t("views.company.workforce_planning.bench.select_job_titles")}
            tokenRenderer={createSingleTokenRenderer({
               selectedValues: selectedJobTitles,
               onClearAll: () => onJobTitlesChange([]),
               I18n,
            })}
         />
         <MultiSelect
            options={tags ?? []}
            getLabel={(tag) => tag.label}
            value={selectedTags}
            onChange={onTagsChange}
            loading={isTagsLoading}
            error={isTagsError ? true : false}
            placeholder={I18n.t("views.company.workforce_planning.bench.select_tags")}
            tokenRenderer={createSingleTokenRenderer({
               selectedValues: selectedTags,
               onClearAll: () => onTagsChange([]),
               I18n,
            })}
         />
      </Flex>
   );
};
